import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import clsx from 'clsx'
import { Icon } from '@/common/components/icon'
import * as styles from './Dialog.css'

/**
 * Dialog component built on Radix UI Dialog primitive.
 *
 * Usage:
 * ```tsx
 * <Dialog open={isOpen} onOpenChange={setIsOpen}>
 *   <Dialog.Trigger asChild>
 *     <Button>Open Dialog</Button>
 *   </Dialog.Trigger>
 *   <Dialog.Content>
 *     <Dialog.Header>
 *       <Dialog.Title>Dialog Title</Dialog.Title>
 *       <Dialog.Close />
 *     </Dialog.Header>
 *     <Dialog.Description>Optional description text.</Dialog.Description>
 *     <div>Dialog body content here</div>
 *     <Dialog.Footer>
 *       <Button variant="secondary" onClick={() => setIsOpen(false)}>Cancel</Button>
 *       <Button>Confirm</Button>
 *     </Dialog.Footer>
 *   </Dialog.Content>
 * </Dialog>
 * ```
 */

// Root
interface DialogProps extends DialogPrimitive.DialogProps {}

function Dialog({ children, ...props }: DialogProps) {
  return <DialogPrimitive.Root {...props}>{children}</DialogPrimitive.Root>
}

// Trigger
interface DialogTriggerProps extends DialogPrimitive.DialogTriggerProps {}

function DialogTrigger({ children, ...props }: DialogTriggerProps) {
  return <DialogPrimitive.Trigger {...props}>{children}</DialogPrimitive.Trigger>
}

// Portal + Overlay + Content wrapper
interface DialogContentProps extends DialogPrimitive.DialogContentProps {
  /** Width of the dialog. Defaults to 500px max. */
  width?: number | string
  /** Whether to show the overlay. Defaults to true. */
  showOverlay?: boolean
}

function DialogContent({
  children,
  width,
  showOverlay = true,
  style,
  className,
  ...props
}: DialogContentProps) {
  return (
    <DialogPrimitive.Portal>
      {showOverlay && <DialogPrimitive.Overlay className={styles.overlay} />}
      <DialogPrimitive.Content
        className={clsx(styles.content, className)}
        style={{
          ...style,
          ...(width ? { maxWidth: width } : {}),
        }}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  )
}

// Header
interface DialogHeaderProps extends React.HTMLAttributes<HTMLDivElement> {}

function DialogHeader({ children, ...props }: DialogHeaderProps) {
  return (
    <div className={styles.header} {...props}>
      {children}
    </div>
  )
}

// Title
interface DialogTitleProps extends DialogPrimitive.DialogTitleProps {}

function DialogTitle({ children, ...props }: DialogTitleProps) {
  return (
    <DialogPrimitive.Title className={styles.title} {...props}>
      {children}
    </DialogPrimitive.Title>
  )
}

// Description
interface DialogDescriptionProps extends DialogPrimitive.DialogDescriptionProps {}

function DialogDescription({ children, ...props }: DialogDescriptionProps) {
  return (
    <DialogPrimitive.Description className={styles.description} {...props}>
      {children}
    </DialogPrimitive.Description>
  )
}

// Close button
interface DialogCloseProps extends Omit<DialogPrimitive.DialogCloseProps, 'children'> {
  /** Screen reader label. Defaults to "Close". */
  label?: string
}

function DialogClose({ label = 'Close', ...props }: DialogCloseProps) {
  return (
    <DialogPrimitive.Close className={styles.closeButton} aria-label={label} {...props}>
      <Icon name="close" size={20} />
    </DialogPrimitive.Close>
  )
}

// Footer
interface DialogFooterProps extends React.HTMLAttributes<HTMLDivElement> {}

function DialogFooter({ children, ...props }: DialogFooterProps) {
  return (
    <div className={styles.footer} {...props}>
      {children}
    </div>
  )
}

// Compose the compound component
Dialog.Trigger = DialogTrigger
Dialog.Content = DialogContent
Dialog.Header = DialogHeader
Dialog.Title = DialogTitle
Dialog.Description = DialogDescription
Dialog.Close = DialogClose
Dialog.Footer = DialogFooter

export { Dialog }
export type {
  DialogProps,
  DialogTriggerProps,
  DialogContentProps,
  DialogHeaderProps,
  DialogTitleProps,
  DialogDescriptionProps,
  DialogCloseProps,
  DialogFooterProps,
}
